<script>
  import { onMount } from "svelte";
  import { link } from "svelte-routing";
  import { pies, piesList, piesLoading } from "../../stores/pies.js";
  import api from "../../services/api.js";

  let pieAllocations = [];
  let allocationsLoading = true;
  let allocationsSaving = false;
  let allocationError = "";
  let allocationSuccess = "";

  // Account balance
  let availableCash = 0;
  let buyingPower = 0;
  let portfolioValue = 0;
  let balanceLoading = true;

  // Investment modal
  let showInvestModal = false;
  let investAmount = "";
  let investLoading = false;
  let investError = "";
  let investSuccess = "";

  // Sell modal
  let showSellModal = false;
  let sellAmount = "";
  let sellLoading = false;
  let sellError = "";
  let sellSuccess = "";

  // ACH modal (deposit/withdraw)
  let showAchModal = false;
  let achType = "deposit"; // "deposit" or "withdraw"
  let achAmount = "";
  let achLoading = false;
  let achError = "";
  let achSuccess = "";
  let achRelationships = [];
  let selectedRelationship = "";
  let achRelationshipsLoading = false;

  onMount(async () => {
    await Promise.all([
      pies.fetchPies(),
      loadAllocations(),
      loadAccountBalance(),
    ]);
  });

  async function loadAccountBalance() {
    balanceLoading = true;
    try {
      const account = await api.getAccountInfo();
      availableCash = parseFloat(account.cash || 0);
      buyingPower = parseFloat(account.buying_power || 0);
      portfolioValue = parseFloat(account.portfolio_value || 0);
    } catch (e) {
      console.error("Failed to load account balance:", e);
    }
    balanceLoading = false;
  }

  async function loadAllocations() {
    allocationsLoading = true;
    try {
      const response = await api.getPieAllocations();
      pieAllocations = response.allocations || [];

      // Initialize allocations for pies that don't have one
      if ($piesList.length > 0 && pieAllocations.length === 0) {
        pieAllocations = [{ pie_index: 0, percentage: 100 }];
      }
    } catch (e) {
      console.error("Failed to load allocations:", e);
      // Default to 100% in first pie
      if ($piesList.length > 0) {
        pieAllocations = [{ pie_index: 0, percentage: 100 }];
      }
    }
    allocationsLoading = false;
  }

  // Moira chart palette as Tailwind bg classes — keep in sync with THEME.md §2
  const colors = [
    "bg-emerald-500", "bg-indigo-500", "bg-amber-500", "bg-pink-500",
    "bg-violet-500", "bg-cyan-500", "bg-red-500", "bg-lime-500"
  ];

  // Rebalance state
  let rebalanceLoading = false;
  let rebalanceError = "";
  let rebalanceSuccess = "";

  // Local allocation values for direct binding (keyed by pie index)
  let localAllocations = {};

  function getColor(index) {
    return colors[index % colors.length];
  }

  // Initialize local allocations when pieAllocations change
  $: {
    const newLocalAllocations = {};
    for (const pie of $piesList) {
      const alloc = pieAllocations.find(a => a.pie_index === pie.index);
      newLocalAllocations[pie.index] = alloc ? alloc.percentage : 0;
    }
    localAllocations = newLocalAllocations;
  }

  function getAllocationForPie(pieIndex) {
    return localAllocations[pieIndex] || 0;
  }

  function handleSliderInput(pieIndex, event) {
    const value = parseFloat(event.target.value) || 0;
    localAllocations[pieIndex] = value;
    localAllocations = { ...localAllocations }; // Trigger reactivity
    syncAllocation(pieIndex, value);
  }

  function handleNumberInput(pieIndex, event) {
    const value = Math.max(0, Math.min(100, parseFloat(event.target.value) || 0));
    localAllocations[pieIndex] = value;
    localAllocations = { ...localAllocations }; // Trigger reactivity
    syncAllocation(pieIndex, value);
  }

  function syncAllocation(pieIndex, percentage) {
    const existingIndex = pieAllocations.findIndex(a => a.pie_index === pieIndex);

    if (existingIndex >= 0) {
      if (percentage === 0) {
        pieAllocations = pieAllocations.filter(a => a.pie_index !== pieIndex);
      } else {
        pieAllocations = pieAllocations.map((a, i) =>
          i === existingIndex ? { ...a, percentage } : a
        );
      }
    } else if (percentage > 0) {
      pieAllocations = [...pieAllocations, { pie_index: pieIndex, percentage }];
    }
  }

  $: totalAllocation = pieAllocations.reduce((sum, a) => sum + a.percentage, 0);
  $: isValidAllocation = Math.abs(totalAllocation - 100) < 0.01;

  async function saveAllocations() {
    if (!isValidAllocation) {
      allocationError = "Allocations must sum to exactly 100%";
      return;
    }

    allocationsSaving = true;
    allocationError = "";
    allocationSuccess = "";

    try {
      await api.updatePieAllocations(pieAllocations);
      allocationSuccess = "Allocations saved successfully!";
      setTimeout(() => allocationSuccess = "", 3000);
    } catch (e) {
      allocationError = e.message || "Failed to save allocations";
    }

    allocationsSaving = false;
  }

  function distributeEvenly() {
    if ($piesList.length === 0) return;

    const evenPercentage = Math.floor(100 / $piesList.length);
    const remainder = 100 - (evenPercentage * $piesList.length);

    pieAllocations = $piesList.map((pie, index) => ({
      pie_index: pie.index,
      percentage: evenPercentage + (index === 0 ? remainder : 0)
    }));
  }

  // Track failed orders to display warnings
  let failedOrders = [];

  async function executeRebalance() {
    rebalanceLoading = true;
    rebalanceError = "";
    rebalanceSuccess = "";
    failedOrders = [];

    try {
      const result = await api.rebalanceAllPies();

      // Check for failed orders
      if (result.orders && Array.isArray(result.orders)) {
        failedOrders = result.orders.filter(order => order.error);
      } else {
        console.log("[REBALANCE] No orders array in response");
      }

      const successCount = result.orders_placed - failedOrders.length;

      if (failedOrders.length > 0) {
        // Some orders failed - show warning
        if (successCount > 0) {
          rebalanceSuccess = `Partial rebalance: ${successCount} orders placed, ${failedOrders.length} failed.`;
        } else {
          rebalanceError = `Rebalance failed: All ${failedOrders.length} orders failed.`;
        }
      } else {
        rebalanceSuccess = `Portfolio rebalanced! ${result.orders_placed} orders placed.`;
      }

      // Refresh balance after rebalance
      await loadAccountBalance();

      // Clear success message after delay (but keep warnings longer)
      setTimeout(() => {
        rebalanceSuccess = "";
        // Clear failed orders after longer delay so user can see them
      }, failedOrders.length > 0 ? 10000 : 5000);

    } catch (e) {
      rebalanceError = e.message || "Rebalance failed";
    }

    rebalanceLoading = false;
  }

  function getFailedOrderReason(error) {
    if (!error) return "Unknown error";
    const errorStr = error.toLowerCase();
    if (errorStr.includes("pattern day trading") || errorStr.includes("pdt")) {
      return "Pattern Day Trading protection - try again tomorrow";
    }
    if (errorStr.includes("skipped") && errorStr.includes("failed sells")) {
      return "Skipped - not enough funds from other sell orders";
    }
    if (errorStr.includes("insufficient")) {
      return "Insufficient funds or shares";
    }
    if (errorStr.includes("market") && errorStr.includes("closed")) {
      return "Market is closed";
    }
    return error;
  }

  function openInvestModal() {
    investAmount = "";
    investError = "";
    investSuccess = "";
    showInvestModal = true;
  }

  function closeInvestModal() {
    showInvestModal = false;
  }

  async function executeInvestment() {
    const amount = parseFloat(investAmount);
    if (isNaN(amount) || amount < 1) {
      investError = "Please enter a valid amount (minimum $1)";
      return;
    }

    // Only validate allocations for multiple pies - single pie is always 100%
    if ($piesList.length > 1 && !isValidAllocation) {
      investError = "Please ensure allocations sum to 100% before investing";
      return;
    }

    investLoading = true;
    investError = "";
    investSuccess = "";

    try {
      const result = await api.investAcrossPies(amount);
      investSuccess = `Successfully invested $${amount}! ${result.total_orders} orders placed.`;
      investAmount = "";
      // Clear success message after a delay
      setTimeout(() => {
        investSuccess = "";
      }, 5000);
    } catch (e) {
      investError = e.message || "Investment failed";
    }

    investLoading = false;
  }
  // Sell functions
  function openSellModal() {
    sellAmount = "";
    sellError = "";
    sellSuccess = "";
    showSellModal = true;
  }

  function closeSellModal() {
    showSellModal = false;
  }

  async function executeSell() {
    const amount = parseFloat(sellAmount);
    if (isNaN(amount) || amount < 1) {
      sellError = "Please enter a valid amount (minimum $1)";
      return;
    }

    sellLoading = true;
    sellError = "";
    sellSuccess = "";

    try {
      const result = await api.sellFromPies(amount);
      sellSuccess = `Successfully sold $${amount}! ${result.total_orders} orders placed. Cash will be available soon.`;
      sellAmount = "";
      // Refresh balance after sell
      await loadAccountBalance();
      setTimeout(() => {
        sellSuccess = "";
      }, 5000);
    } catch (e) {
      sellError = e.message || "Sell failed";
    }

    sellLoading = false;
  }

  // ACH functions (deposit/withdraw)
  async function openAchModal(type) {
    achType = type;
    achAmount = "";
    achError = "";
    achSuccess = "";
    selectedRelationship = "";
    showAchModal = true;

    // Load ACH relationships
    achRelationshipsLoading = true;
    try {
      const response = await api.getAchRelationships();
      achRelationships = response.relationships || [];
      if (achRelationships.length > 0) {
        selectedRelationship = achRelationships[0].id;
      }
    } catch (e) {
      console.error("Failed to load ACH relationships:", e);
      achRelationships = [];
    }
    achRelationshipsLoading = false;
  }

  function closeAchModal() {
    showAchModal = false;
  }

  async function executeAchTransfer() {
    const amount = parseFloat(achAmount);
    if (isNaN(amount) || amount < 1) {
      achError = "Please enter a valid amount (minimum $1)";
      return;
    }

    if (!selectedRelationship) {
      achError = "Please select a bank account";
      return;
    }

    achLoading = true;
    achError = "";
    achSuccess = "";

    try {
      if (achType === "deposit") {
        await api.deposit(amount, selectedRelationship);
        achSuccess = `Deposit of $${amount.toFixed(2)} initiated! Funds will be available in 1-3 business days.`;
      } else {
        await api.withdraw(amount, selectedRelationship);
        achSuccess = `Withdrawal of $${amount.toFixed(2)} initiated! Funds will arrive in 1-3 business days.`;
      }
      achAmount = "";
      // Refresh balance
      await loadAccountBalance();
    } catch (e) {
      achError = e.message || `${achType === "deposit" ? "Deposit" : "Withdrawal"} failed`;
    }

    achLoading = false;
  }
</script>

<header class="mb-6 flex items-start justify-between gap-4">
  <div>
    <h1 class="text-2xl font-bold text-ink">Pies</h1>
    <p class="text-sm text-ink-secondary mt-1">Allocate your portfolio across measured portions, then invest, sell, and rebalance.</p>
  </div>
  <a use:link href="/admin/pies/new" class="btn btn-primary btn-sm shrink-0">
    <i class="fas fa-plus"></i>
    New pie
  </a>
</header>

<!-- Account balance -->
<section class="card card-pad mb-6">
  <h2 class="text-lg font-semibold text-ink mb-4">Account balance</h2>
  {#if balanceLoading}
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="skeleton h-14"></div>
      <div class="skeleton h-14"></div>
      <div class="skeleton h-14"></div>
      <div class="skeleton h-14"></div>
    </div>
  {:else}
    <dl class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <dt class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Cash balance</dt>
        <dd class="text-xl font-semibold text-ink tnum mt-1">${availableCash.toFixed(2)}</dd>
      </div>
      <div>
        <dt class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Pending orders</dt>
        <dd class="text-xl font-semibold text-ink tnum mt-1">${Math.max(0, availableCash - buyingPower).toFixed(2)}</dd>
      </div>
      <div>
        <dt class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Available to invest</dt>
        <dd class="text-xl font-semibold text-ink tnum mt-1">${buyingPower.toFixed(2)}</dd>
      </div>
      <div>
        <dt class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Portfolio value</dt>
        <dd class="text-xl font-semibold text-ink tnum mt-1">${portfolioValue.toFixed(2)}</dd>
      </div>
    </dl>

    <div class="mt-6 pt-4 border-t border-line">
      <div class="flex flex-wrap gap-3">
        <button
          on:click={() => openAchModal('deposit')}
          class="btn btn-outline btn-sm"
        >
          <i class="fas fa-plus-circle text-ink-tertiary"></i> Deposit
        </button>
        <button
          on:click={() => openAchModal('withdraw')}
          disabled={availableCash < 1}
          class="btn btn-outline btn-sm"
        >
          <i class="fas fa-minus-circle text-ink-tertiary"></i> Withdraw
        </button>
      </div>
      <p class="text-xs text-ink-tertiary mt-3">
        Deposit brings money from your bank. Withdraw sends money to your bank. ACH transfers take 1-3 business days.
      </p>
    </div>
  {/if}
</section>

<!-- Invest & sell (always show if there are pies) -->
{#if $piesList.length > 0}
  <section class="card card-pad mb-6">
    <div class="flex flex-wrap items-start justify-between gap-4 mb-4">
      <div>
        <h2 class="text-lg font-semibold text-ink">Invest &amp; sell</h2>
        <p class="text-sm text-ink-secondary mt-1">
          {#if $piesList.length === 1}
            Invest available cash or sell positions according to your pie allocations
          {:else}
            Set how your investments are distributed across pies, then invest or sell
          {/if}
        </p>
      </div>
      <div class="flex items-center gap-2">
        {#if $piesList.length > 1}
          <button
            on:click={distributeEvenly}
            class="btn btn-ghost btn-sm"
          >
            <i class="fas fa-balance-scale text-ink-tertiary"></i> Distribute evenly
          </button>
        {/if}
        <button
          on:click={executeRebalance}
          disabled={rebalanceLoading || portfolioValue < 1}
          class="btn btn-outline btn-sm"
          title="Rebalance portfolio to match target allocations"
        >
          {#if rebalanceLoading}
            <i class="fas fa-spinner fa-spin"></i> Rebalancing...
          {:else}
            <i class="fas fa-sync-alt text-ink-tertiary"></i> Rebalance
          {/if}
        </button>
      </div>
    </div>

    {#if allocationsLoading}
      <div class="space-y-3 mb-4">
        <div class="skeleton h-14"></div>
        <div class="skeleton h-14"></div>
      </div>
    {:else}
      <!-- Pie allocations (show for multiple pies) -->
      {#if $piesList.length > 1}
        <div class="space-y-2 mb-4">
          {#each $piesList as pie, index}
            <div class="flex items-center gap-4 rounded-xl bg-surface-variant p-3">
              <span class="w-2.5 h-2.5 rounded-full {getColor(index)} shrink-0"></span>
              <div class="flex-1 min-w-0">
                <span class="text-sm font-medium text-ink">{pie.name}</span>
                <span class="text-xs text-ink-tertiary ml-2">({pie.allocations?.length || 0} stocks)</span>
              </div>
              <div class="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={localAllocations[pie.index] || 0}
                  on:input={(e) => handleSliderInput(pie.index, e)}
                  class="w-32 accent-moira cursor-pointer"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={localAllocations[pie.index] || 0}
                  on:input={(e) => handleNumberInput(pie.index, e)}
                  class="input !w-24 !py-2 text-right tnum !bg-surface"
                />
                <span class="text-sm text-ink-secondary">%</span>
              </div>
            </div>
          {/each}
        </div>

        <!-- Total validation -->
        <div class="flex items-center gap-2 mb-4 text-sm">
          <span class="font-medium text-ink">Total</span>
          <span class="font-semibold tnum {isValidAllocation ? 'text-emerald-600' : 'text-red-600'}">
            {totalAllocation.toFixed(0)}%
          </span>
          {#if !isValidAllocation}
            <span class="text-xs text-red-600">
              <i class="fas fa-exclamation-circle mr-1"></i>
              Must equal 100%
            </span>
          {:else}
            <span class="text-xs text-emerald-600">
              <i class="fas fa-check-circle mr-1"></i>
              Valid
            </span>
          {/if}
        </div>
      {:else}
        <!-- Single pie info -->
        <div class="flex items-center gap-4 rounded-xl bg-surface-variant p-3 mb-4">
          <span class="w-2.5 h-2.5 rounded-full {getColor(0)} shrink-0"></span>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-ink">{$piesList[0].name}</span>
            <span class="text-xs text-ink-tertiary ml-2">({$piesList[0].allocations?.length || 0} stocks)</span>
          </div>
          <span class="text-sm font-semibold text-emerald-600 tnum">100%</span>
        </div>
      {/if}

      <!-- Investment input and buttons -->
      <div class="pt-4 border-t border-line">
        <div class="flex flex-wrap items-end gap-3">
          <div class="flex-1 min-w-48">
            <label class="label">
              Amount
            </label>
            <div class="relative">
              <span class="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-tertiary">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                bind:value={investAmount}
                placeholder="100.00"
                class="input !pl-8 tnum"
              />
            </div>
            <p class="text-xs text-ink-tertiary mt-1.5">
              Available to invest: <span class="font-medium text-emerald-600 tnum">${buyingPower.toFixed(2)}</span>
              {#if buyingPower < availableCash}
                <span class="text-amber-600 ml-1">(pending orders)</span>
              {/if}
            </p>
          </div>
          <button
            on:click={executeInvestment}
            disabled={investLoading || !investAmount || parseFloat(investAmount) < 1 || parseFloat(investAmount) > buyingPower || ($piesList.length > 1 && !isValidAllocation)}
            class="btn btn-primary"
            title="Use available cash to buy stocks"
          >
            {#if investLoading}
              <i class="fas fa-spinner fa-spin"></i> Investing...
            {:else}
              <i class="fas fa-arrow-up"></i> Invest
            {/if}
          </button>
          <button
            on:click={openSellModal}
            disabled={portfolioValue < 1}
            class="btn btn-outline text-red-600 hover:text-red-700"
            title="Sell stocks to get cash"
          >
            <i class="fas fa-arrow-down"></i> Sell
          </button>
          {#if $piesList.length > 1}
            <button
              on:click={saveAllocations}
              disabled={!isValidAllocation || allocationsSaving}
              class="btn btn-ghost"
            >
              {#if allocationsSaving}
                <i class="fas fa-spinner fa-spin"></i>
              {:else}
                <i class="fas fa-save text-ink-tertiary"></i>
              {/if}
              Save %
            </button>
          {/if}
        </div>

        <!-- Preview of how money will be distributed -->
        {#if investAmount && parseFloat(investAmount) >= 1 && ($piesList.length === 1 || isValidAllocation)}
          <div class="mt-4 rounded-xl bg-surface-variant p-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-2">Investment preview</p>
            {#if $piesList.length === 1}
              <div class="flex justify-between text-sm text-ink">
                <span>{$piesList[0].name}</span>
                <span class="font-medium tnum">${parseFloat(investAmount).toFixed(2)}</span>
              </div>
            {:else}
              {#each pieAllocations.filter(a => a.percentage > 0) as alloc}
                {@const pie = $piesList.find(p => p.index === alloc.pie_index)}
                {@const pieAmount = (parseFloat(investAmount) * alloc.percentage / 100).toFixed(2)}
                {#if pie}
                  <div class="flex justify-between text-sm text-ink py-1">
                    <span>{pie.name} <span class="text-ink-tertiary tnum">({alloc.percentage}%)</span></span>
                    <span class="font-medium tnum">${pieAmount}</span>
                  </div>
                {/if}
              {/each}
            {/if}
          </div>
        {/if}

        {#if investError}
          <p class="text-sm text-red-600 mt-2"><i class="fas fa-exclamation-triangle mr-1"></i> {investError}</p>
        {/if}
        {#if investSuccess}
          <p class="text-sm text-emerald-600 mt-2"><i class="fas fa-check-circle mr-1"></i> {investSuccess}</p>
        {/if}
        {#if allocationError}
          <p class="text-sm text-red-600 mt-2"><i class="fas fa-exclamation-triangle mr-1"></i> {allocationError}</p>
        {/if}
        {#if allocationSuccess}
          <p class="text-sm text-emerald-600 mt-2"><i class="fas fa-check-circle mr-1"></i> {allocationSuccess}</p>
        {/if}
        {#if rebalanceError}
          <p class="text-sm text-red-600 mt-2"><i class="fas fa-exclamation-triangle mr-1"></i> {rebalanceError}</p>
        {/if}
        {#if rebalanceSuccess}
          <p class="text-sm text-emerald-600 mt-2"><i class="fas fa-check-circle mr-1"></i> {rebalanceSuccess}</p>
        {/if}
        {#if failedOrders.length > 0}
          <div class="banner banner-warning mt-3">
            <p class="font-semibold mb-2">
              <i class="fas fa-exclamation-circle mr-1"></i> Some orders could not be completed:
            </p>
            <ul class="space-y-1">
              {#each failedOrders as order}
                <li class="flex items-start">
                  <span class="font-medium mr-2">{order.symbol}:</span>
                  <span>{getFailedOrderReason(order.error)}</span>
                </li>
              {/each}
            </ul>
          </div>
        {/if}
      </div>
    {/if}
  </section>
{/if}

<!-- My pies -->
<section class="card card-pad">
  <h2 class="text-lg font-semibold text-ink mb-4">My pies</h2>
  {#if $piesLoading}
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div class="skeleton h-28"></div>
      <div class="skeleton h-28"></div>
      <div class="skeleton h-28"></div>
    </div>
  {:else if $piesList.length === 0}
    <div class="flex flex-col items-center text-center py-12">
      <i class="fas fa-chart-pie text-2xl text-ink-tertiary mb-3"></i>
      <p class="text-sm text-ink-secondary">No pies yet. Create your first pie to get started.</p>
      <a use:link href="/admin/pies/new" class="btn btn-primary btn-sm mt-4">
        Create your first pie
      </a>
    </div>
  {:else}
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {#each $piesList as pie, index}
        <a use:link href="/admin/pies/{pie.index}" class="card p-4 block hover:border-ink-tertiary transition-colors">
          <div class="flex items-center gap-3 mb-3">
            <span class="w-2.5 h-2.5 rounded-full {getColor(index)} shrink-0"></span>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-medium text-ink truncate">{pie.name}</h3>
              <p class="text-xs text-ink-tertiary">{pie.allocations?.length || 0} holdings</p>
            </div>
            {#if $piesList.length > 1}
              <div class="text-right shrink-0">
                <span class="text-sm font-semibold tnum {(localAllocations[pie.index] || 0) > 0 ? 'text-emerald-600' : 'text-ink-tertiary'}">
                  {localAllocations[pie.index] || 0}%
                </span>
                <p class="text-xs text-ink-tertiary">allocation</p>
              </div>
            {/if}
          </div>
          <div class="flex flex-wrap gap-1">
            {#each (pie.allocations || []).slice(0, 5) as allocation}
              <span class="badge badge-neutral tnum">
                {allocation.symbol} {allocation.percentage}%
              </span>
            {/each}
            {#if (pie.allocations || []).length > 5}
              <span class="badge badge-neutral">
                +{pie.allocations.length - 5} more
              </span>
            {/if}
          </div>
        </a>
      {/each}
    </div>
  {/if}
</section>

<!-- Investment modal -->
{#if showInvestModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-ink/50" on:click={closeInvestModal}></div>
    <div class="relative card shadow-sm w-full max-w-md mx-4 p-6">
      <button on:click={closeInvestModal} class="absolute top-4 right-4 text-ink-tertiary hover:text-ink">
        <i class="fas fa-times"></i>
      </button>

      <h3 class="text-lg font-semibold text-ink mb-2">
        Invest across pies
      </h3>

      <p class="text-sm text-ink-secondary mb-4">
        Enter the amount to invest. It will be distributed across your pies according to your allocation settings.
      </p>

      <!-- Show allocation preview -->
      <div class="rounded-xl bg-surface-variant p-4 mb-4">
        <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-2">Distribution preview</p>
        {#each pieAllocations.filter(a => a.percentage > 0) as alloc}
          {@const pie = $piesList.find(p => p.index === alloc.pie_index)}
          {#if pie}
            <div class="flex justify-between text-sm text-ink py-1">
              <span>{pie.name}</span>
              <span class="font-medium tnum">{alloc.percentage}%</span>
            </div>
          {/if}
        {/each}
      </div>

      <div class="mb-4">
        <label class="label">
          Amount to invest
        </label>
        <div class="relative">
          <span class="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-tertiary">$</span>
          <input
            type="number"
            min="1"
            step="0.01"
            bind:value={investAmount}
            placeholder="100.00"
            class="input !pl-8 tnum"
          />
        </div>
      </div>

      {#if investAmount && parseFloat(investAmount) >= 1}
        <div class="rounded-xl bg-emerald-50 p-4 mb-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Per-pie amounts</p>
          {#each pieAllocations.filter(a => a.percentage > 0) as alloc}
            {@const pie = $piesList.find(p => p.index === alloc.pie_index)}
            {@const pieAmount = (parseFloat(investAmount) * alloc.percentage / 100).toFixed(2)}
            {#if pie}
              <div class="flex justify-between text-sm text-emerald-700 py-1">
                <span>{pie.name}</span>
                <span class="font-medium tnum">${pieAmount}</span>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if investError}
        <p class="text-sm text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle mr-1"></i> {investError}
        </p>
      {/if}

      {#if investSuccess}
        <p class="text-sm text-emerald-600 mb-4">
          <i class="fas fa-check-circle mr-1"></i> {investSuccess}
        </p>
      {/if}

      <div class="flex justify-end gap-2">
        <button
          on:click={closeInvestModal}
          class="btn btn-ghost btn-sm"
        >
          Cancel
        </button>
        <button
          on:click={executeInvestment}
          disabled={investLoading || !investAmount || parseFloat(investAmount) < 1}
          class="btn btn-primary btn-sm"
        >
          {#if investLoading}
            <i class="fas fa-spinner fa-spin"></i> Investing...
          {:else}
            Invest
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Sell modal -->
{#if showSellModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-ink/50" on:click={closeSellModal}></div>
    <div class="relative card shadow-sm w-full max-w-md mx-4 p-6">
      <button on:click={closeSellModal} class="absolute top-4 right-4 text-ink-tertiary hover:text-ink">
        <i class="fas fa-times"></i>
      </button>

      <h3 class="text-lg font-semibold text-ink mb-2">
        Sell from pies
      </h3>

      <p class="text-sm text-ink-secondary mb-4">
        Sell stocks from your pies proportionally based on your allocation settings. The proceeds will become available cash.
      </p>

      <!-- Current portfolio value -->
      <div class="rounded-xl bg-surface-variant p-4 mb-4">
        <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-1">Current positions value</p>
        <p class="text-lg font-semibold text-ink tnum">${(portfolioValue - availableCash).toFixed(2)}</p>
      </div>

      <div class="mb-4">
        <label class="label">
          Amount to sell
        </label>
        <div class="relative">
          <span class="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-tertiary">$</span>
          <input
            type="number"
            min="1"
            step="0.01"
            bind:value={sellAmount}
            placeholder="100.00"
            class="input !pl-8 tnum"
          />
        </div>
      </div>

      {#if sellAmount && parseFloat(sellAmount) >= 1}
        <div class="rounded-xl bg-red-50 p-4 mb-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-red-700 mb-2">Sell preview</p>
          {#each pieAllocations.filter(a => a.percentage > 0) as alloc}
            {@const pie = $piesList.find(p => p.index === alloc.pie_index)}
            {@const pieAmount = (parseFloat(sellAmount) * alloc.percentage / 100).toFixed(2)}
            {#if pie}
              <div class="flex justify-between text-sm text-red-700 py-1">
                <span>{pie.name}</span>
                <span class="font-medium tnum">-${pieAmount}</span>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if sellError}
        <p class="text-sm text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle mr-1"></i> {sellError}
        </p>
      {/if}

      {#if sellSuccess}
        <p class="text-sm text-emerald-600 mb-4">
          <i class="fas fa-check-circle mr-1"></i> {sellSuccess}
        </p>
      {/if}

      <div class="flex justify-end gap-2">
        <button
          on:click={closeSellModal}
          class="btn btn-ghost btn-sm"
        >
          Cancel
        </button>
        <button
          on:click={executeSell}
          disabled={sellLoading || !sellAmount || parseFloat(sellAmount) < 1}
          class="btn btn-danger btn-sm"
        >
          {#if sellLoading}
            <i class="fas fa-spinner fa-spin"></i> Selling...
          {:else}
            Sell
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- ACH deposit/withdraw modal -->
{#if showAchModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-ink/50" on:click={closeAchModal}></div>
    <div class="relative card shadow-sm w-full max-w-md mx-4 p-6">
      <button on:click={closeAchModal} class="absolute top-4 right-4 text-ink-tertiary hover:text-ink">
        <i class="fas fa-times"></i>
      </button>

      <h3 class="text-lg font-semibold text-ink mb-2">
        {#if achType === "deposit"}
          Deposit funds
        {:else}
          Withdraw funds
        {/if}
      </h3>

      <p class="text-sm text-ink-secondary mb-4">
        {#if achType === "deposit"}
          Transfer money from your bank to your Moira Finance brokerage account.
        {:else}
          Transfer money from your Moira Finance brokerage account to your bank.
        {/if}
      </p>

      {#if achType === "withdraw"}
        <div class="rounded-xl bg-surface-variant p-4 mb-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-1">Available cash</p>
          <p class="text-lg font-semibold text-ink tnum">${availableCash.toFixed(2)}</p>
        </div>
      {/if}

      {#if achRelationshipsLoading}
        <div class="space-y-3 mb-4">
          <div class="skeleton h-11"></div>
          <div class="skeleton h-11"></div>
        </div>
      {:else if achRelationships.length === 0}
        <div class="banner banner-warning mb-4">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          No bank accounts linked. Please contact support to link a bank account for ACH transfers.
        </div>
      {:else}
        <div class="mb-4">
          <label class="label">
            Bank account
          </label>
          <select
            bind:value={selectedRelationship}
            class="select"
          >
            {#each achRelationships as rel}
              <option value={rel.id}>
                {rel.nickname || rel.account_owner_name} ({rel.bank_account_type})
              </option>
            {/each}
          </select>
        </div>

        <div class="mb-4">
          <label class="label">
            Amount
          </label>
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-tertiary">$</span>
            <input
              type="number"
              min="1"
              step="0.01"
              bind:value={achAmount}
              placeholder="100.00"
              class="input !pl-8 tnum"
            />
          </div>
        </div>
      {/if}

      <p class="text-xs text-ink-tertiary mb-4">
        <i class="fas fa-clock mr-1"></i>
        ACH transfers typically take 1-3 business days to complete.
      </p>

      {#if achError}
        <p class="text-sm text-red-600 mb-4">
          <i class="fas fa-exclamation-triangle mr-1"></i> {achError}
        </p>
      {/if}

      {#if achSuccess}
        <p class="text-sm text-emerald-600 mb-4">
          <i class="fas fa-check-circle mr-1"></i> {achSuccess}
        </p>
      {/if}

      <div class="flex justify-end gap-2">
        <button
          on:click={closeAchModal}
          class="btn btn-ghost btn-sm"
        >
          Cancel
        </button>
        <button
          on:click={executeAchTransfer}
          disabled={achLoading || !achAmount || parseFloat(achAmount) < 1 || !selectedRelationship || (achType === "withdraw" && parseFloat(achAmount) > availableCash)}
          class="btn btn-primary btn-sm"
        >
          {#if achLoading}
            <i class="fas fa-spinner fa-spin"></i> Processing...
          {:else if achType === "deposit"}
            Deposit
          {:else}
            Withdraw
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}
